import {createContext, useCallback, use, useEffect, useMemo, useRef, type ReactNode, useState} from 'react'
import type {ConcreteRequest} from 'relay-runtime'
import {DEFAULT_RELAY_CACHE_TTL} from '../helpers'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useRelayEnvironment} from 'react-relay'
import {fetchQueryWithCache} from '../query-helpers'

type RelayCachePreheatContextType = {
  markQueryForPreheating: (
    query: ConcreteRequest,
    variables: Record<string, unknown>,
    source: string,
    excludeFromCacheKey?: string[],
    highPriority?: boolean,
  ) => void
  preHeatQueries: () => void
}

type RelayCachePreheatContextProviderType = {
  children: ReactNode
}

// This is just a safeguard agains extreme cases (like an issue that has a thousands of issues links in comments)
export const MAX_QUERIES_TO_MARK_FOR_PREHEATING = 50

const PREHEAT_REQUESTS_BATCH_SIZE = 4

const RelayCachePreheatContext = createContext<RelayCachePreheatContextType | null>(null)

export function RelayCachePreheatContextProvider({children}: RelayCachePreheatContextProviderType) {
  const relayEnvironment = useRelayEnvironment()
  const queriesToPreheatRef = useRef<
    Record<
      string,
      {query: ConcreteRequest; variables: Record<string, unknown>; source: string; excludeFromCacheKey?: string[]}
    >
  >({})
  const keysRef = useRef<Set<string>>(new Set())
  const prioritizedKeysRef = useRef<Set<string>>(new Set())
  const preheatedRef = useRef<Set<string>>(new Set())
  const [pending, setPending] = useState(false)

  const preheatQuery = useCallback(
    async (key: string): Promise<boolean> => {
      if (!preheatedRef.current.has(key)) {
        const queryToPreheat = queriesToPreheatRef.current[key]
        if (queryToPreheat) {
          preheatedRef.current.add(key)
          try {
            await fetchQueryWithCache(
              relayEnvironment,
              queryToPreheat.query,
              queryToPreheat.variables,
              {fetchPolicy: 'store-or-network'},
              DEFAULT_RELAY_CACHE_TTL,
              queryToPreheat.source,
              false,
              queryToPreheat.excludeFromCacheKey,
            ).toPromise()
          } catch {
            // Silently handle errors - preheat failures shouldn't break the app
          }
          return true
        }
      }
      return false
    },
    [relayEnvironment],
  )

  const markQueryForPreheating = useCallback(
    (
      query: ConcreteRequest,
      variables: Record<string, unknown>,
      source: string,
      excludeFromCacheKey?: string[],
      highPriority?: boolean,
    ) => {
      const queryName = query.fragment.name
      // generate a key from the query name and sorted variables to avoid duplicates
      const filteredVariables = {...variables}
      if (excludeFromCacheKey && excludeFromCacheKey.length > 0) {
        for (const key of excludeFromCacheKey) {
          delete filteredVariables[key]
        }
      }
      const key = `${queryName}#${JSON.stringify(
        Object.fromEntries(Object.entries(filteredVariables).sort(([a], [b]) => (a < b ? -1 : 1))),
      )}`
      if (highPriority) {
        if (!prioritizedKeysRef.current.has(key)) {
          queriesToPreheatRef.current[key] = {query, variables, source, excludeFromCacheKey}
          prioritizedKeysRef.current.add(key)
        }
      } else if (!keysRef.current.has(key)) {
        queriesToPreheatRef.current[key] = {query, variables, source, excludeFromCacheKey}
        keysRef.current.add(key)
      }
      setPending(true)
    },
    [],
  )

  const preheatQuerySet = useCallback(
    (promises: Array<Promise<boolean>>, querySet: Set<string>) => {
      for (const key of querySet) {
        if (promises.length >= PREHEAT_REQUESTS_BATCH_SIZE) {
          break
        }
        if (!preheatedRef.current.has(key)) {
          promises.push(preheatQuery(key))
        }
      }
      return promises
    },
    [preheatQuery],
  )

  const preHeatQueries = useCallback(async () => {
    const promises = preheatQuerySet([], prioritizedKeysRef.current)
    preheatQuerySet(promises, keysRef.current)
    await Promise.all(promises)
    if (preheatedRef.current.size >= prioritizedKeysRef.current.size + keysRef.current.size) {
      // All queries have been preheated
      setPending(false)
    }
  }, [preheatQuerySet])

  useEffect(() => {
    let cancelled = false

    const scheduleNextPreheat = () => {
      if (cancelled) return

      if (!('requestIdleCallback' in window)) {
        return
      }
      // eslint-disable-next-line compat/compat
      requestIdleCallback(() => {
        if (cancelled) return
        if (!pending) return
        const runPreheat = async () => {
          await preHeatQueries()
          scheduleNextPreheat()
        }
        void runPreheat()
      })
    }

    scheduleNextPreheat()

    return () => {
      cancelled = true
    }
  }, [preHeatQueries, pending])

  const contextValue: RelayCachePreheatContextType = useMemo(() => {
    return {
      markQueryForPreheating,
      preHeatQueries,
    }
  }, [markQueryForPreheating, preHeatQueries])

  return <RelayCachePreheatContext value={contextValue}>{children}</RelayCachePreheatContext>
}

export const useRelayCachePreheatContext = () => {
  const context = use(RelayCachePreheatContext)
  if (!context) {
    // Fail silently to avoid issues in tests or if used outside of provider
    // eslint-disable-next-line no-console
    console.warn('useRelayCachePreheatContext must be used within a RelayCachePreheatContextProvider')
    return {
      markQueryForPreheating: () => {},
      preHeatQueries: () => {},
    }
  }

  return context
}

try{ RelayCachePreheatContext.displayName ||= 'RelayCachePreheatContext' } catch {}
try{ RelayCachePreheatContextProvider.displayName ||= 'RelayCachePreheatContextProvider' } catch {}